import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Button, Form, Input, InputNumber, Select, Space, Spin, Switch } from 'antd';
import { handleApiSuccess, handleApiError } from 'utils';
import { timeTrackerUserGroupsListService } from 'services/time_tracker';
import {
  schedulingScheduleCreateService,
  schedulingScheduleGetService,
  schedulingScheduleUpdateService,
} from 'services/scheduling';
import { getTimezoneSelectOptions } from 'pages/time_tracker/tracker/tracker_timezone';

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 },
};
const tailLayout = {
  wrapperCol: { offset: 6, span: 14 },
};

const { Option } = Select;

function memberSearchName(member) {
  if (!member || typeof member === 'string') return '';
  return [
    member.display_name,
    member.first_name,
    member.last_name,
    member.email,
  ]
    .filter(Boolean)
    .join(' ');
}

function groupSearchText(group) {
  const names = (group.members || []).map(memberSearchName).filter(Boolean);
  return [group.group_name, ...names].join(' ');
}

function groupOptionLabel(group) {
  const count =
    group.members?.length != null ? ` (${group.members.length})` : '';
  return `${group.group_name || 'Group'}${count}`;
}

export default function ScheduleForm(props) {
  const { id } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(!!id);
  const history = useHistory();
  const [form] = Form.useForm();
  const [userGroups, setUserGroups] = useState([]);
  const [tzSearch, setTzSearch] = useState('');
  const timezoneValue = Form.useWatch('timezone', form);
  const zones = useMemo(
    () => getTimezoneSelectOptions(tzSearch, timezoneValue),
    [tzSearch, timezoneValue],
  );

  useEffect(() => {
    timeTrackerUserGroupsListService()
      .then((res) => setUserGroups(res.data?.items || []))
      .catch(handleApiError);

    if (!id) {
      setLoadingRecord(false);
      return;
    }

    schedulingScheduleGetService(id)
      .then((res) => {
        const item = res.data?.items?.[0];
        if (!item) return;
        form.setFieldsValue({
          name: item.name,
          timezone: item.timezone || undefined,
          status: item.status || 'ENABLED',
          member_groups: (item.member_groups || []).map((g) => g._id || g),
          manager_groups: (item.manager_groups || []).map((g) => g._id || g),
          miss_clock_notify: item.miss_clock_notify !== false,
          miss_clock_grace_minutes: item.miss_clock_grace_minutes || 15,
        });
      })
      .catch(handleApiError)
      .finally(() => setLoadingRecord(false));
  }, [id, form]);

  const onFinish = (values) => {
    setIsLoading(true);
    const payload = {
      name: values.name,
      timezone: values.timezone || '',
      status: values.status,
      member_groups: values.member_groups || [],
      manager_groups: values.manager_groups || [],
      miss_clock_notify: values.miss_clock_notify !== false,
      miss_clock_grace_minutes: values.miss_clock_grace_minutes || 15,
    };
    const req = id
      ? schedulingScheduleUpdateService(id, payload)
      : schedulingScheduleCreateService(payload);
    req
      .then((res) => {
        setIsLoading(false);
        handleApiSuccess(res);
        const newId = res.data?.items?.[0]?._id || id;
        if (!id && newId) {
          history.push('/time-tracker/scheduling/' + newId + '/board');
        } else {
          history.push('/time-tracker/scheduling');
        }
      })
      .catch((err) => {
        setIsLoading(false);
        handleApiError(err);
      });
  };

  const sortedGroups = useMemo(
    () =>
      [...userGroups].sort((a, b) =>
        (a.group_name || '').localeCompare(b.group_name || '', undefined, {
          sensitivity: 'base',
        }),
      ),
    [userGroups],
  );

  if (loadingRecord) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {userGroups.length === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24, maxWidth: 720 }}
          message="No user groups yet"
          description={
            <span>
              Create groups under Time Tracker → User groups, then assign them
              here as members or managers.{' '}
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => history.push('/time-tracker/user-groups/create')}
              >
                Create a user group
              </Button>
            </span>
          }
        />
      )}

      <Form
        {...layout}
        form={form}
        name="schedule"
        onFinish={onFinish}
        initialValues={{
          status: 'ENABLED',
          member_groups: [],
          manager_groups: [],
          miss_clock_notify: true,
          miss_clock_grace_minutes: 15,
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Schedule name is required',
            },
          ]}
        >
          <Input placeholder="e.g. Call answering" maxLength={120} />
        </Form.Item>

        <Form.Item
          label="Status"
          name="status"
          rules={[{ required: true, message: 'Status is required' }]}
          extra="Enabled = live for staff clock-in. Disabled = hide this schedule from clock-in without deleting it. This is not the same as Publish (publish is done on the weekly board)."
        >
          <Select style={{ maxWidth: 240 }}>
            <Option value="ENABLED">Enabled</Option>
            <Option value="DISABLED">Disabled</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Timezone"
          name="timezone"
          extra="Common US timezones are listed first. Search to find any other timezone. Leave empty to use each viewer’s browser timezone."
        >
          <Select
            allowClear
            showSearch
            filterOption={false}
            optionFilterProp="label"
            placeholder="Browser default"
            options={zones}
            style={{ maxWidth: 420 }}
            onSearch={setTzSearch}
            onDropdownVisibleChange={(open) => {
              if (!open) setTzSearch('');
            }}
          />
        </Form.Item>

        <Form.Item
          label="Member groups"
          name="member_groups"
          extra="People in these groups can view this schedule and claim open shifts. Create the group under Time Tracker → User groups, then pick it here. Search matches the group name or a member’s name."
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Select member groups"
            style={{ maxWidth: 420 }}
          >
            {sortedGroups.map((g) => (
              <Option key={g._id} value={g._id} label={groupSearchText(g)}>
                {groupOptionLabel(g)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Manager groups"
          name="manager_groups"
          extra="People in these groups can edit and publish this schedule. Create that group under Time Tracker → User groups (this is separate from Member groups), then pick it here. If this is empty, anyone with Scheduling edit permission can manage the schedule. Search matches the group name or a member’s name."
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Select manager groups"
            style={{ maxWidth: 420 }}
          >
            {sortedGroups.map((g) => (
              <Option key={g._id} value={g._id} label={groupSearchText(g)}>
                {groupOptionLabel(g)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Missed clock-in email"
          name="miss_clock_notify"
          valuePropName="checked"
          extra="Email schedule managers if an assigned published shift has no clock-in after the grace period."
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label="Grace minutes"
          name="miss_clock_grace_minutes"
          extra="Wait this many minutes after shift start before sending the missed clock-in email."
        >
          <InputNumber min={0} max={240} style={{ maxWidth: 160 }} />
        </Form.Item>

        <Form.Item {...tailLayout}>
          <Space size="middle">
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {id ? 'Update' : 'Create'}
            </Button>
            <Button onClick={() => history.push('/time-tracker/scheduling')}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
